import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useQuery } from '@tanstack/react-query';

import { useTrackAIJob } from 'hooks/useTrackAIJob';
import { AIJobType } from 'models/AIJob';
import Admission from 'models/reviews/Admission';
import { admissionQueryKeys, admissionQueryParams } from 'services/api/admissions';
import { showReview } from 'services/api/reviews';
import { AIJobNotificationType, TrackedJob } from 'stores/aiJobNotificationStore';

export default function useAdmissionSlideout() {
  const { admissionId } = useParams<{ admissionId: string }>();
  const { startTracking } = useTrackAIJob();

  const { data: admission } = useQuery({
    queryKey: admissionQueryKeys.show({ id: admissionId }),
    queryFn: () => showReview(admissionQueryParams.show(admissionId!)) as Promise<Admission>,
    enabled: !!admissionId,
  });

  // Show the full-page loading screen for initial processing only.
  // When a job is a retry, the tabs stay visible with inline loading indicators instead.
  const [initialLoadingDone, setInitialLoadingDone] = useState(false);

  const isProcessingInitialJobs = !!(
    (admission?.summaryJobForCurrentAttachments?.isProcessing && !admission.summaryJobForCurrentAttachments.isRetry) ||
    (admission?.criteriaEvaluationJob?.isProcessing && !admission.criteriaEvaluationJob.isRetry)
  );

  const showFullLoading = isProcessingInitialJobs && !initialLoadingDone;

  const handleInitialLoadingDone = useCallback(() => {
    setInitialLoadingDone(true);
  }, []);

  useEffect(() => {
    if (!admission) return;

    const jobs = [
      admission.summaryJobForCurrentAttachments?.isProcessing
        ? { id: admission.summaryJobForCurrentAttachments.id, type: AIJobType.SUMMARY }
        : null,
      admission.criteriaEvaluationJob?.isProcessing
        ? { id: admission.criteriaEvaluationJob.id, type: AIJobType.CRITERIA_EVALUATION }
        : null,
    ].filter((j): j is TrackedJob => j !== null);

    // `startTracking` de-dupes via the store: if the incoming job ids are a subset
    // of what's already tracked for this key, it's a no-op. Retries produce new job
    // ids, so this call will replace the tracked context and resubscribe.
    if (jobs.length > 0) {
      startTracking({
        jobs,
        jobType: AIJobNotificationType.ADMISSION_SUMMARY,
        jobbableId: admission.id,
        returnUrl: `/admissions/${admission.id}`,
      });
    }
  }, [admission, startTracking]);

  return {
    admissionId,
    admission,
    showFullLoading,
    handleInitialLoadingDone,
    attachmentsCount: admission?.attachments.length,
  };
}
